import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ChecklistInputType,
  ChecklistType,
  createInquiryChecklist,
  fetchInquiryChecklist,
  updateInquiryChecklist,
} from '../../api/inquiryChecklists';
import {
  VehicleBrand,
  fetchVehicleBrands,
} from '../../api/vehicleBrands';
import {
  VehicleModel,
  fetchVehicleModels,
} from '../../api/vehicleModels';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';
// Lightweight inline modal wrapper for this editor
const InlineModal: FC<{ title: string; onClose: () => void; children: any }> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/30" onClick={onClose} />
    <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-4 shadow-xl">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        <button onClick={onClose} className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-slate-300">Close</button>
      </div>
      {children}
    </div>
  </div>
);

interface ChecklistFormState {
  name: string;
  description: string;
  inputType: ChecklistInputType;
  answers: { answer: string; bgColor?: string }[];
  brandIds: string[];
  modelIds: string[];
  type: ChecklistType;
  isMandatory: boolean;
}

const buildInitialFormState = (): ChecklistFormState => ({
  name: '',
  description: '',
  inputType: 'boolean',
  answers: [],
  brandIds: [],
  modelIds: [],
  type: 'inspection',
  isMandatory: false,
});

const buildFormStateFromPayload = (payload: {
  name: string;
  description: string | null;
  inputType: ChecklistInputType;
  answerOptions: (string | { answer: string; bgColor?: string })[] | null;
  brands?: VehicleBrand[];
  models?: VehicleModel[];
  type: ChecklistType;
  isMandatory: boolean;
}): ChecklistFormState => ({
  name: payload.name,
  description: payload.description ?? '',
  inputType: payload.inputType,
  answers: (payload.answerOptions ?? []).map((o) =>
    typeof o === 'string' ? { answer: o } : { answer: o.answer, bgColor: o.bgColor },
  ),
  brandIds: (payload.brands ?? []).map((brand) => brand.id),
  modelIds: (payload.models ?? []).map((model) => model.id),
  type: payload.type ?? 'inspection',
  isMandatory: payload.isMandatory ?? false,
});

const parseOptions = (value: string): string[] =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const VehicleInquiryChecklistEditor: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const mode: 'create' | 'edit' = id === 'add' || !id ? 'create' : 'edit';

  const duplicateState = useMemo<ChecklistFormState | null>(() => {
    if (mode !== 'create') {
      return null;
    }

    const duplicateId = searchParams.get('duplicateId');
    if (!duplicateId) {
      return null;
    }

    const readStringArray = (raw: string | null): string[] => {
      if (!raw) {
        return [];
      }

      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed.map((entry) => String(entry));
        }
      } catch (error) {
        // Ignore malformed data and fall back to empty array.
      }

      return [];
    };

    const parseInputType = (value: string | null): ChecklistInputType => {
      switch (value) {
        case 'boolean':
        case 'text':
        case 'single_select':
        case 'multi_select':
          return value;
        default:
          return 'boolean';
      }
    };

    const parseChecklistType = (value: string | null): ChecklistType => {
      switch (value) {
        case 'inspection':
        case 'pre_delivery':
          return value;
        default:
          return 'inspection';
      }
    };

    return {
      name: searchParams.get('name') ?? '',
      description: searchParams.get('description') ?? '',
      inputType: parseInputType(searchParams.get('inputType')),
      answers: readStringArray(searchParams.get('options')).map((s) => ({ answer: s })),
      brandIds: readStringArray(searchParams.get('brandIds')),
      modelIds: readStringArray(searchParams.get('modelIds')),
      type: parseChecklistType(searchParams.get('type')),
      isMandatory: (searchParams.get('isMandatory') ?? '').toLowerCase() === 'true',
    };
  }, [mode, searchParams]);

  const [form, setForm] = useState<ChecklistFormState>(
    duplicateState ?? buildInitialFormState(),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [brandOptions, setBrandOptions] = useState<VehicleBrand[]>([]);
  const [brandOptionsError, setBrandOptionsError] = useState<string | null>(null);
  const [brandOptionsLoading, setBrandOptionsLoading] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');

  const [modelOptions, setModelOptions] = useState<VehicleModel[]>([]);
  const [modelOptionsError, setModelOptionsError] = useState<string | null>(null);
  const [modelOptionsLoading, setModelOptionsLoading] = useState(false);
  const [modelSearch, setModelSearch] = useState('');

  const [answerModal, setAnswerModal] = useState<
    | { index: number | null; value: { answer: string; bgColor?: string } }
    | null
  >(null);

  const brandSelectionKey = useMemo(
    () => form.brandIds.join('|'),
    [form.brandIds],
  );
  const modelOptionKey = useMemo(
    () => modelOptions.map((model) => model.id).join('|'),
    [modelOptions],
  );

  const fetchAllBrands = useCallback(async () => {
    setBrandOptionsLoading(true);
    setBrandOptionsError(null);

    try {
      const first = await fetchVehicleBrands({ page: 1, limit: 100 });
      let data = [...first.data];
      const { totalPages } = first.meta;
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await fetchVehicleBrands({ page, limit: 100 });
        data = data.concat(next.data);
      }
      setBrandOptions(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load brands.';
      setBrandOptionsError(message);
    } finally {
      setBrandOptionsLoading(false);
    }
  }, []);

  const fetchModelsForBrands = useCallback(async (brandIds: string[]) => {
    if (!brandIds.length) {
      setModelOptions([]);
      setModelOptionsError(null);
      return;
    }

    setModelOptionsLoading(true);
    setModelOptionsError(null);

    try {
      let collected: VehicleModel[] = [];
      for (const brandId of brandIds) {
        const first = await fetchVehicleModels({ page: 1, limit: 100, brandId });
        collected = collected.concat(first.data);
        const { totalPages } = first.meta;
        for (let page = 2; page <= totalPages; page += 1) {
          const next = await fetchVehicleModels({ page, limit: 100, brandId });
          collected = collected.concat(next.data);
        }
      }

      const deduped = Array.from(
        new Map(collected.map((model) => [model.id, model])).values(),
      );

      setModelOptions(deduped);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load models.';
      setModelOptionsError(message);
    } finally {
      setModelOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllBrands();
  }, [fetchAllBrands]);

  useEffect(() => {
    if (duplicateState) {
      setForm(duplicateState);
    }
  }, [duplicateState]);

  useEffect(() => {
    if (form.brandIds.length === 0) {
      setModelOptions([]);
      setModelOptionsError(null);
      return;
    }

    fetchModelsForBrands(form.brandIds);
  }, [brandSelectionKey, fetchModelsForBrands, form.brandIds]);

  useEffect(() => {
    if (modelOptions.length === 0) {
      setForm((prev) => {
        if (prev.modelIds.length === 0) {
          return prev;
        }
        return {
          ...prev,
          modelIds: [],
        };
      });
      return;
    }

    const allowed = new Set(modelOptions.map((model) => model.id));
    setForm((prev) => {
      if (prev.modelIds.length === 0) {
        return prev;
      }

      const filtered = prev.modelIds.filter((modelId) => allowed.has(modelId));
      if (filtered.length === prev.modelIds.length) {
        return prev;
      }
      return {
        ...prev,
        modelIds: filtered,
      };
    });
  }, [modelOptionKey, modelOptions]);

  useEffect(() => {
    if (mode !== 'edit' || !id || id === 'add') {
      return;
    }

    let active = true;

    setLoading(true);
    fetchInquiryChecklist(id)
      .then((response) => {
        if (!active) {
          return;
        }
        const nextForm = buildFormStateFromPayload(response);
        setForm(nextForm);
        if (nextForm.brandIds.length) {
          fetchModelsForBrands(nextForm.brandIds);
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Unable to load checklist.';
        setError(message);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [fetchModelsForBrands, id, mode]);

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInputTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextType = event.target.value as ChecklistInputType;
    setForm((prev) => ({
      ...prev,
      inputType: nextType,
    }));
  };

  const addOrEditAnswer = (index: number | null, value: { answer: string; bgColor?: string }) => {
    setForm((prev) => {
      const list = [...prev.answers];
      const trimmed = value.answer.trim();
      const normalised = { answer: trimmed, bgColor: value.bgColor || undefined };
      if (index === null) {
        list.push(normalised);
      } else {
        list[index] = normalised;
      }
      return { ...prev, answers: list };
    });
  };

  const removeAnswer = (index: number) => {
    setForm((prev) => {
      const list = [...prev.answers];
      list.splice(index, 1);
      return { ...prev, answers: list };
    });
  };

  const handleChecklistTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value as ChecklistType;
    setForm((prev) => ({
      ...prev,
      type: next,
    }));
  };

  const handleMandatoryChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({
      ...prev,
      isMandatory: event.target.checked,
    }));
  };

  const toggleSelection = (field: 'brandIds' | 'modelIds', value: string) => {
    setForm((prev) => {
      const next = new Set(prev[field]);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }

      return {
        ...prev,
        [field]: Array.from(next),
      };
    });
  };

  const filteredBrandOptions = useMemo(() => {
    const query = brandSearch.trim().toLowerCase();
    if (!query) {
      return brandOptions;
    }
    return brandOptions.filter((brand) =>
      brand.name.toLowerCase().includes(query),
    );
  }, [brandOptions, brandSearch]);

  const filteredModelOptions = useMemo(() => {
    const query = modelSearch.trim().toLowerCase();
    if (!query) {
      return modelOptions;
    }
    return modelOptions.filter((model) => {
      const brandName = model.brand?.name?.toLowerCase() ?? '';
      return (
        model.name.toLowerCase().includes(query) ||
        brandName.includes(query)
      );
    });
  }, [modelOptions, modelSearch]);

  const brandSummary = useMemo(() => {
    if (form.brandIds.length === 0) {
      return 'All brands';
    }
    return `${form.brandIds.length} selected`;
  }, [form.brandIds.length]);

  const modelSummary = useMemo(() => {
    if (form.modelIds.length === 0) {
      return form.brandIds.length === 0
        ? 'All models'
        : 'No models selected';
    }
    return `${form.modelIds.length} selected`;
  }, [form.brandIds.length, form.modelIds.length]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const name = form.name.trim();
      if (!name) {
        throw new Error('Checklist name is required.');
      }

      const payload: {
        name: string;
        description?: string;
        inputType: ChecklistInputType;
        answerOptions?: string[];
        brandIds?: string[];
        modelIds?: string[];
        type: ChecklistType;
        isMandatory?: boolean;
      } = {
        name,
        inputType: form.inputType,
        type: form.type,
        isMandatory: form.isMandatory,
      };

      const description = form.description.trim();
      if (description) {
        payload.description = description;
      }

      if (form.inputType === 'single_select' || form.inputType === 'multi_select') {
        const options = form.answers
          .map((o) => ({ answer: o.answer.trim(), bgColor: o.bgColor || undefined }))
          .filter((o) => o.answer.length > 0);
        if (!options.length) {
          throw new Error('Provide at least one answer option.');
        }
        payload.answerOptions = options as any;
      }

      if (form.brandIds.length) {
        payload.brandIds = form.brandIds;
      }

      if (form.modelIds.length) {
        payload.modelIds = form.modelIds;
      }

      if (mode === 'create') {
        await createInquiryChecklist(payload);
      } else if (id) {
        await updateInquiryChecklist(id, payload);
      }

      navigate('/vehicle-management/checklists');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save checklist.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      
      <PageHeaderWithBack
        title={mode === 'create' ? 'Add checklist' : 'Edit checklist'}
        description={<p className="mt-2 max-w-2xl text-sm text-slate-500">
          Define reusable job-offer questions and optionally scope them to specific vehicle brands or models.
        </p>}
        onBack={() => navigate('/vehicle-management/checklists')}
        backLabel="Back to checklists"
      />

      {error ? <p className="mt-4 text-sm text-danger">{error}</p> : null}

      <form onSubmit={handleSubmit} className="mt-6 grid gap-6 md:grid-cols-2" noValidate>
        <label className="flex flex-col gap-1 text-sm text-slate-600">
          <span>Name</span>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleInputChange}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            disabled={submitting || loading}
            required
          />
        </label>

        <label className="flex flex-col gap-1 text-sm text-slate-600">
          <span>Field type</span>
          <select
            name="inputType"
            value={form.inputType}
            onChange={handleInputTypeChange}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            disabled={submitting || loading}
          >
            <option value="boolean">Yes / No</option>
            <option value="text">Free text</option>
            <option value="single_select">Single select</option>
            <option value="multi_select">Multi select</option>
          </select>
        </label>

      
        <label className="flex flex-col gap-1 text-sm text-slate-600">
            <span>Checklist type</span>
            <select
              value={form.type}
              onChange={handleChecklistTypeChange}
              className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
              disabled={submitting || loading}
            >
              <option value="inspection" className='px-3'>Inspection</option>
              <option value="pre_delivery" className='px-3'>Pre-delivery</option>
            </select>
          </label>

          <label className="flex items-center gap-3 text-sm text-slate-600 md:self-end justify-start mb-3">
            <input
              type="checkbox"
              checked={form.isMandatory}
              onChange={handleMandatoryChange}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              disabled={submitting || loading}
            />
            <span className="leading-none">Response is required (mandatory)</span>
          </label>
        

        <label className="flex flex-col gap-1 text-sm text-slate-600 md:col-span-2">
          <span>Description (optional)</span>
          <textarea
            name="description"
            value={form.description}
            onChange={handleInputChange}
            rows={3}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            disabled={submitting || loading}
          />
        </label>

        {(form.inputType === 'single_select' || form.inputType === 'multi_select') && (
          <div className="md:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Answer options</span>
              <button
                type="button"
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary"
                onClick={() => setAnswerModal({ index: null, value: { answer: '', bgColor: undefined } })}
                disabled={submitting || loading}
              >
                + Add answer
              </button>
            </div>
            {form.answers.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
                No answers added yet.
              </div>
            ) : (
              <ul className="space-y-2">
                {form.answers.map((opt, idx) => (
                  <li key={idx} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="flex items-center gap-3">
                      <span className="inline-block h-4 w-4 rounded" style={{ backgroundColor: opt.bgColor || '#ffffff', outline: '1px solid #e5e7eb' }} />
                      <span className="text-sm text-slate-700">{opt.answer}</span>
                      {opt.bgColor ? (
                        <span className="text-xs text-slate-400">{opt.bgColor}</span>
                      ) : (
                        <span className="text-xs text-slate-400">(no color)</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary"
                        onClick={() => setAnswerModal({ index: idx, value: opt })}
                        disabled={submitting || loading}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="rounded-md border border-slate-200 px-2 py-1 text-xs text-danger hover:border-danger"
                        onClick={() => removeAnswer(idx)}
                        disabled={submitting || loading}
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-1 text-xs text-slate-400">These options will be shown to sales agents when capturing responses.</p>
          </div>
        )}

        <div className="flex flex-col gap-2 text-sm text-slate-600">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-medium text-slate-700">Linked brands</span>
              <p className="text-xs text-slate-400">{brandSummary}</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              onClick={fetchAllBrands}
              disabled={brandOptionsLoading}
            >
              <span aria-hidden="true">↻</span>
              Refresh brands
            </button>
          </div>

          <input
            type="search"
            value={brandSearch}
            onChange={(event) => setBrandSearch(event.target.value)}
            placeholder="Search brands..."
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
            disabled={brandOptionsLoading}
          />

          <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-inner shadow-primary/5">
            {brandOptionsLoading ? (
              <p className="text-xs text-slate-400">Loading brands…</p>
            ) : brandOptionsError ? (
              <p className="text-xs text-danger">{brandOptionsError}</p>
            ) : filteredBrandOptions.length === 0 ? (
              <p className="text-xs text-slate-400">No matching brands.</p>
            ) : (
              filteredBrandOptions.map((brand) => {
                const checked = form.brandIds.includes(brand.id);
                return (
                  <label
                    key={brand.id}
                    className="flex items-center gap-2 rounded-lg px-2 py-1 text-slate-600 hover:bg-primary/5"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                      checked={checked}
                      onChange={() => toggleSelection('brandIds', brand.id)}
                      disabled={submitting || loading}
                    />
                    <span>{brand.name}</span>
                  </label>
                );
              })
            )}
          </div>
          <span className="text-xs text-slate-400">
            Leave empty to make this question available for every brand.
          </span>
        </div>

        

        <div className="flex flex-col gap-2 text-sm text-slate-600">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <span className="font-medium text-slate-700">Linked models</span>
              <p className="text-xs text-slate-400">{modelSummary}</p>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => fetchModelsForBrands(form.brandIds)}
              disabled={modelOptionsLoading || form.brandIds.length === 0}
            >
              <span aria-hidden="true">↻</span>
              Refresh models
            </button>
          </div>

          {form.brandIds.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500">
              Select at least one brand to choose specific models.
            </div>
          ) : (
            <>
              <input
                type="search"
                value={modelSearch}
                onChange={(event) => setModelSearch(event.target.value)}
                placeholder="Search models..."
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                disabled={modelOptionsLoading}
              />

              <div className="max-h-52 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 shadow-inner shadow-primary/5">
                {modelOptionsLoading ? (
                  <p className="text-xs text-slate-400">Loading models…</p>
                ) : modelOptionsError ? (
                  <p className="text-xs text-danger">{modelOptionsError}</p>
                ) : filteredModelOptions.length === 0 ? (
                  <p className="text-xs text-slate-400">No matching models.</p>
                ) : (
                  filteredModelOptions.map((model) => {
                    const checked = form.modelIds.includes(model.id);
                    return (
                      <label
                        key={model.id}
                        className="flex items-center gap-2 rounded-lg px-2 py-1 text-slate-600 hover:bg-primary/5"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                          checked={checked}
                          onChange={() => toggleSelection('modelIds', model.id)}
                          disabled={submitting || loading}
                        />
                        <span className="flex-1">
                          {model.name}
                          <span className="ml-2 text-xs text-slate-400">{model.brand?.name ?? '—'}</span>
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}

          <span className="text-xs text-slate-400">
            Leave empty to apply this question to every model.
          </span>
        </div>

        <div className="md:col-span-2">
          <button
            type="submit"
            className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
            disabled={submitting || loading}
          >
            {submitting
              ? 'Saving…'
              : mode === 'create'
                ? 'Create checklist'
                : 'Save changes'}
          </button>
        </div>
      </form>

      {answerModal && (
        <InlineModal onClose={() => setAnswerModal(null)} title={answerModal.index === null ? 'Add answer' : 'Edit answer'}>
          <AnswerModalContent
            initial={answerModal.value}
            onCancel={() => setAnswerModal(null)}
            onSave={(val) => {
              addOrEditAnswer(answerModal.index, val);
              setAnswerModal(null);
            }}
          />
        </InlineModal>
      )}
    </div>
  );
};

const AnswerModalContent: FC<{
  initial: { answer: string; bgColor?: string };
  onCancel: () => void;
  onSave: (val: { answer: string; bgColor?: string }) => void;
}> = ({ initial, onCancel, onSave }) => {
  const [answer, setAnswer] = useState(initial.answer);
  const [bgColor, setBgColor] = useState(initial.bgColor || '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = answer.trim();
    if (!trimmed) {
      setError('Answer is required.');
      return;
    }
    setError(null);
    const color = bgColor.trim();
    onSave({ answer: trimmed, bgColor: color || undefined });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <label className="flex flex-col gap-1 text-sm text-slate-600">
        <span>Answer</span>
        <input
          type="text"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          required
        />
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-sm text-slate-600">
          <span>Background color (optional)</span>
          <input
            type="color"
            value={/^#[0-9a-fA-F]{6}$/.test(bgColor) ? bgColor : '#ffffff'}
            onChange={(e) => setBgColor(e.target.value)}
            className="h-10 w-full rounded border border-slate-200"
          />
        </label>

        <label className="flex flex-col gap-1 text-sm text-slate-600">
          <span>Hex code</span>
          <input
            type="text"
            placeholder="#RRGGBB"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </label>
      </div>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:border-slate-300">Cancel</button>
        <button type="submit" className="rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/90">Save</button>
      </div>
    </form>
  );
};

export default VehicleInquiryChecklistEditor;
